import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import StudyMateLogo from "../components/StudyMateLogo";

/**
 * Google OAuth callback handler.
 * After user signs in with Google, they land here with ?code=...&state=...
 * We extract those params and exchange them with the backend for a JWT.
 */
export default function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const { loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState(null);
  const hasExchanged = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React StrictMode
    if (hasExchanged.current) return;
    hasExchanged.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    // User cancelled or Google returned an error
    if (errorParam) {
      setError("Sign-in cancelled or failed");
      setTimeout(() => nav("/login"), 2000);
      return;
    }

    // Missing required params
    if (!code) {
      setError("Invalid OAuth response");
      setTimeout(() => nav("/login"), 2000);
      return;
    }

    // Exchange code for JWT token
    const exchange = async () => {
      try {
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        await loginWithGoogle(code, state, redirectUri);
        nav("/"); // Success - go to home page
      } catch (err) {
        console.error("Google OAuth exchange failed:", err);
        setError(err?.response?.data?.detail || "Google sign-in failed");
        setTimeout(() => nav("/login"), 3000);
      }
    };

    exchange();
  }, [searchParams, loginWithGoogle, nav]);

  return (
    <div className="min-h-screen grid place-items-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="ba-card w-full max-w-md p-8 relative text-center"
      >
        <div className="flex justify-center mb-5">
          <StudyMateLogo compact />
        </div>

        {error ? (
          <>
            <div className="text-rose-400 font-mono text-sm mb-4">{error}</div>
            <div className="text-zinc-500 text-xs">Redirecting to login...</div>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-cyan mx-auto mb-4" />
            <div className="font-mono text-xs tracking-wider text-zinc-400 uppercase">
              Completing Google Sign-In...
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
